import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { HtmlTypography } from 'src/components/HtmlTypography';
import { TextField } from 'src/components/TextField';
import { simpleFormat } from 'src/utils/simpleFormat';

export interface SignWaiverProps {
  text: string;
  initials?: string[];
  signed?: boolean;
  onUpdateInitials: (initials: string[]) => void;
}

export const SignWaiver: React.FC<SignWaiverProps> = ({
  text,
  initials = [],
  signed = false,
  onUpdateInitials,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'userAssessmentQuestion',
  });

  const splitText = text.split('{{initials}}');

  const endsWithInitials =
    text.endsWith('{{initials}}') && splitText[splitText.length - 1] === '';

  const handleInitialChange = (index: number, value: string) => {
    const updatedInitials = [...initials];
    updatedInitials[index] = value;
    onUpdateInitials(updatedInitials);
  };

  if (splitText.length === 1) {
    return <HtmlTypography>{simpleFormat(text, true)}</HtmlTypography>;
  }

  return (
    <Box>
      {splitText.map((segment, index) => {
        const isLastSegment = index === splitText.length - 1;
        const shouldShowInitials = !isLastSegment;

        return (
          <Box key={index} component="span">
            <HtmlTypography component="span">
              {simpleFormat(segment, true)}
            </HtmlTypography>
            {shouldShowInitials && (
              <Box
                component="span"
                sx={{ display: 'inline-block', mx: 1, verticalAlign: 'middle' }}
              >
                {signed ? (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      minWidth: '100px',
                      fontWeight: 'bold',
                    }}
                  >
                    {initials[index] || ''}
                  </Box>
                ) : (
                  <TextField
                    value={initials[index] || ''}
                    onChange={(e) => handleInitialChange(index, e.target.value)}
                    placeholder={t('initials')}
                  />
                )}
              </Box>
            )}
          </Box>
        );
      })}
      {endsWithInitials && (
        <Box component="span" sx={{ display: 'inline-block', mx: 1 }}>
          {signed ? (
            <Box
              component="span"
              sx={{ display: 'inline-block', minWidth: '100px' }}
            >
              {initials[splitText.length - 1] || ''}
            </Box>
          ) : (
            <TextField
              value={initials[splitText.length - 1] || ''}
              onChange={(e) =>
                handleInitialChange(splitText.length - 1, e.target.value)
              }
              placeholder={t('initials')}
              sx={{ width: '100px', display: 'inline-flex' }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};
